// useContactData.js - Version optimisée
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;

// Cache global pour les données de contact
let contactCache = {
  data: null,
  lastFetch: 0,
  isLoading: false,
  loadPromise: null
};

// Temps de cache en millisecondes (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

const useContactData = () => {
  const [data, setData] = useState(() => {
    // Initialiser immédiatement avec le cache si disponible
    return contactCache.data || {
      prenom_contact: '',
      nom_contact: '',
      profession: '',
      email_contact: '',
      telephone_contact: '',
      photo_contact: null,
      url_pro: '',
      is_visible: false
    };
  });

  const [loading, setLoading] = useState(() => {
    // Ne pas être en loading si on a déjà des données en cache
    return !contactCache.data;
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savingError, setSavingError] = useState(null);

  const saveTimeoutRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);

  const token = localStorage.getItem("access_token");

  // Vérifier si les données en cache sont encore valides
  const isCacheValid = () => {
    const now = Date.now();
    return contactCache.data && (now - contactCache.lastFetch) < CACHE_DURATION;
  };

  // Charger les données de contact avec optimisation
  const loadData = useCallback(async (useCache = true) => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Si déjà en cours de chargement, réutiliser la promesse
    if (contactCache.isLoading && contactCache.loadPromise) {
      try {
        const result = await contactCache.loadPromise;
        setData(result);
        setLoading(false);
        return;
      } catch (error) {
        setError(error.response?.data?.error || 'Erreur lors du chargement des informations de contact');
        setLoading(false);
        return;
      }
    }

    // Utiliser le cache si valide et demandé
    if (useCache && isCacheValid()) {
      setData(contactCache.data);
      setLoading(false);
      return;
    }

    // Marquer comme en cours de chargement
    contactCache.isLoading = true;
    
    // Créer la promesse de chargement
    contactCache.loadPromise = (async () => {
      try {
        // Ne pas afficher le loading si on a déjà des données en cache
        if (!contactCache.data) {
          setLoading(true);
        }
        setError(null);

        const response = await axios.get(`${USER_BACKEND_API_BASE_URL}/users/contact`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const contactData = {
          prenom_contact: response.data.prenom_contact || '',
          nom_contact: response.data.nom_contact || '',
          profession: response.data.profession || '',
          email_contact: response.data.email_contact || '',
          telephone_contact: response.data.telephone_contact || '',
          photo_contact: response.data.photo_contact || null,
          url_pro: response.data.url_pro || '',
          is_visible: response.data.is_visible || false
        };

        // Mettre à jour le cache global
        contactCache.data = contactData;
        contactCache.lastFetch = Date.now();
        
        return contactData;
      } finally {
        contactCache.isLoading = false;
        contactCache.loadPromise = null;
      }
    })();

    try {
      const result = await contactCache.loadPromise;
      setData(result);
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur lors du chargement des informations de contact');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fonction de sauvegarde optimisée
  const saveData = useCallback(async () => {
    if (!token || saving || !hasUnsavedChangesRef.current) return;

    setSaving(true);
    setSavingError(null);

    try {
      await axios.post(
        `${USER_BACKEND_API_BASE_URL}/users/contact`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Mettre à jour le cache après sauvegarde réussie
      contactCache.data = data;
      contactCache.lastFetch = Date.now();

      // Réinitialiser le flag de changements en attente
      hasUnsavedChangesRef.current = false;
    } catch (error) {
      setSavingError(error.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }, [token, data, saving]);

  // Fonction de mise à jour avec debouncing automatique
  const updateData = useCallback((updates) => {
    setData(prev => ({ ...prev, ...updates }));

    // Marquer les données comme modifiées
    hasUnsavedChangesRef.current = true;
    setError(null);
    setSavingError(null);

    // Programmer la sauvegarde avec debouncing
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveData, 1500);
  }, [saveData]);

  // Fonction pour forcer le rechargement des données
  const refreshData = useCallback(async () => {
    await loadData(false);
  }, [loadData]);

  // Fonction pour invalider le cache
  const invalidateCache = useCallback(() => {
    contactCache = {
      data: null,
      lastFetch: 0,
      isLoading: false,
      loadPromise: null
    };
  }, []);

  // Chargement initial optimisé
  useEffect(() => {
    if (token) {
      // Charger en arrière-plan si pas de cache valide
      if (!isCacheValid()) {
        loadData();
      }
    }
  }, [token, loadData]);

  // Nettoyage du timeout à la destruction du composant
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    data,
    loading,
    saving,
    error,
    savingError,
    updateData,
    refreshData,
    invalidateCache,
    saveData,
    isAuthenticated: !!token,
    hasUnsavedChanges: hasUnsavedChangesRef.current
  };
};

export default useContactData;